import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ImpersonationProvider } from "@/contexts/ImpersonationContext";
import ProtectedRoute from "@/components/ProtectedRoute";

import Index from "./pages/Index";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import StudentSignup from "./pages/StudentSignup";
import Dashboard from "./pages/Dashboard";
import Admission from "./pages/Admission";
import Administration from "./pages/Administration";
import Academic from "./pages/Academic";
import Examination from "./pages/Examination";
import Accounting from "./pages/Accounting";
import StudentServices from "./pages/StudentServices";
import Discipline from "./pages/Discipline";
import UserManagement from "./pages/UserManagement";
import StudentPreRegistration from "./pages/StudentPreRegistration";
import EmailAudit from "./pages/EmailAudit";
import AcceptInvitation from "./pages/AcceptInvitation";
import AuditLog from "./pages/AuditLog";
import SessionSummaryAuditLog from "./pages/SessionSummaryAuditLog";
import Communication from "./pages/Communication";
import InviteLanding from "./pages/InviteLanding";
import PublicSurvey from "./pages/PublicSurvey";
import StudentFile from "./pages/StudentFile";
import Attendance from "./pages/Attendance";
import ChitChat from "./pages/ChitChat";

import NotFound from "./pages/NotFound";
import { AttendanceAccessGate } from "./components/attendance/AttendanceAccessGate";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { OfflineBanner } from "./components/OfflineBanner";
import { PWAUpdateNotifier } from "./components/PWAUpdateNotifier";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
  },
});

function AppRoutes() {
  const { realRoles } = useAuth();
  const isSuperadmin = realRoles.includes("superadmin");

  return (
    <ImpersonationProvider isSuperadmin={isSuperadmin}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/student-signup" element={<StudentSignup />} />
        <Route path="/accept-invitation" element={<AcceptInvitation />} />
        <Route path="/invite" element={<InviteLanding />} />
        <Route path="/join" element={<InviteLanding />} />
        <Route path="/welcome" element={<InviteLanding />} />
        <Route path="/survey/:slug" element={<PublicSurvey />} />
        <Route path="/student-file/:id" element={
          <ProtectedRoute allowedRoles={["superadmin", "auditor", "principal", "deputy_principal", "head_admission", "head_administration", "head_academic", "head_examination", "head_accounting", "head_student_service", "head_discipline", "hod_cmt", "hod_pst", "hod_sw", "it_incharge"]}>
            <StudentFile />
          </ProtectedRoute>
        } />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/admission" element={
          <ProtectedRoute allowedRoles={["superadmin", "auditor", "principal", "deputy_principal", "head_admission"]}>
            <Admission />
          </ProtectedRoute>
        } />
        <Route path="/administration" element={
          <ProtectedRoute allowedRoles={["superadmin", "auditor", "principal", "deputy_principal", "head_administration"]}>
            <Administration />
          </ProtectedRoute>
        } />
        <Route path="/academic" element={
          <ProtectedRoute allowedRoles={["superadmin", "auditor", "principal", "deputy_principal", "head_academic", "hod_cmt", "hod_pst", "hod_sw", "it_incharge", "student"]}>
            <Academic />
          </ProtectedRoute>
        } />
        <Route path="/examination" element={
          <ProtectedRoute allowedRoles={["superadmin", "auditor", "principal", "deputy_principal", "head_examination", "hod_cmt", "hod_pst", "hod_sw", "it_incharge", "student"]}>
            <Examination />
          </ProtectedRoute>
        } />
        <Route path="/accounting" element={
          <ProtectedRoute allowedRoles={["superadmin", "auditor", "principal", "deputy_principal", "head_accounting", "student"]}>
            <Accounting />
          </ProtectedRoute>
        } />
        <Route path="/student-services" element={
          <ProtectedRoute allowedRoles={["superadmin", "auditor", "principal", "deputy_principal", "head_student_service", "student"]}>
            <StudentServices />
          </ProtectedRoute>
        } />
        <Route path="/discipline" element={
          <ProtectedRoute allowedRoles={["superadmin", "auditor", "principal", "deputy_principal", "head_discipline", "student"]}>
            <Discipline />
          </ProtectedRoute>
        } />
        <Route path="/user-management" element={
          <ProtectedRoute allowedRoles={["superadmin", "auditor"]}>
            <UserManagement />
          </ProtectedRoute>
        } />
        <Route path="/student-pre-registration" element={
          <ProtectedRoute allowedRoles={["superadmin", "auditor"]}>
            <StudentPreRegistration />
          </ProtectedRoute>
        } />
        <Route path="/admin/email-audit" element={
          <ProtectedRoute allowedRoles={["superadmin", "auditor", "head_administration"]}>
            <EmailAudit />
          </ProtectedRoute>
        } />
        <Route path="/communication" element={
          <ProtectedRoute allowedRoles={["superadmin", "auditor"]}>
            <Communication />
          </ProtectedRoute>
        } />
        <Route path="/attendance" element={
          <ProtectedRoute allowedRoles={["superadmin", "auditor", "principal", "deputy_principal", "head_administration", "head_academic", "it_incharge", "security_man", "hod_cmt", "hod_pst", "hod_sw", "cr_cmt4", "cr_cmt5", "cr_cmt6", "cr_pst4", "cr_pst5", "cr_pst6", "cr_sw4", "cr_sw5", "cr_sw6"]}>
            <AttendanceAccessGate>
              <Attendance />
            </AttendanceAccessGate>
          </ProtectedRoute>
        } />
        <Route path="/audit-log" element={
          <ProtectedRoute allowedRoles={["superadmin", "auditor", "principal", "deputy_principal"]}>
            <AuditLog />
          </ProtectedRoute>
        } />
        <Route path="/audit-log/session-summaries" element={
          <ProtectedRoute allowedRoles={["superadmin", "auditor", "principal", "deputy_principal", "head_academic", "head_administration"]}>
            <SessionSummaryAuditLog />
          </ProtectedRoute>
        } />
        
        <Route path="/chitchat" element={<ProtectedRoute><ChitChat /></ProtectedRoute>} />
        <Route path="/chitchat/c/:conversationId" element={<ProtectedRoute><ChitChat /></ProtectedRoute>} />
        <Route path="/chitchat/c/:conversationId/m/:messageId" element={<ProtectedRoute><ChitChat /></ProtectedRoute>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </ImpersonationProvider>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
      <OfflineBanner />
      <PWAInstallPrompt />
      <PWAUpdateNotifier />
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
